import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { dataLayer } from '../../../data/dataLayer';
import { X, Activity, ArrowUpRight, ArrowDownRight, ArrowRight, Clock, Target, Zap, CheckCircle2, AlertTriangle, UserMinus, Unlink, StickyNote, BarChart3, ClipboardCheck, LifeBuoy, Copy } from 'lucide-react';
import { pseudonymFor } from '../../shared/learnerPseudonym';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { type ModuleId, MODULES, getModuleIcon } from '../../../content';
import { GroupId } from '../../../config/systemConfig';
import { getRecentSessionsForLearner, getAllModuleTrends, getWeeklyActivityForLearner, getWeeklyStats } from '../../../utils/activityHelpers';
import { getDashboardStats } from '../../../utils/dashboardStats';
import { ActivityTimeline } from '../../../components/shared/ActivityTimeline';
import { WeeklyActivityDots } from '../../../components/shared/WeeklyActivityDots';
import { DjayNarrator } from '../../../mascot';
import { InsightBar } from '../components/InsightBar';
import { AppState } from '../../../app/types';
import { useFocusTrap } from '../../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { getAssessmentHistoryForLearner, getReport, type AssessmentHistoryEntry, type AssessmentReport } from '../../../assessment';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { getStudentInsights } from '../utils/differentiationInsights';
// ============================================================
// Weekly accuracy trend helper (SD-1)
// ============================================================
interface WeekAccuracy {
  label: string;
  accuracy: number;
  questionCount: number;
}
function getWeeklyAccuracyTrend(questionStates: Record<string, {
  history?: Array<{
    timestamp: number;
    isCorrect: boolean;
  }>;
}>, weeks: number = 4): WeekAccuracy[] {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const thisMonday = new Date(now);
  thisMonday.setHours(0, 0, 0, 0);
  thisMonday.setDate(thisMonday.getDate() + mondayOffset);
  const thisMondayTs = thisMonday.getTime();
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  const weekLabels = ['4w geleden', '3w geleden', '2w geleden', 'Vorige week', 'Deze week'];
  const result: WeekAccuracy[] = [];
  for (let i = weeks; i >= 0; i--) {
    const weekStart = thisMondayTs - i * MS_PER_WEEK;
    const weekEnd = weekStart + MS_PER_WEEK;
    let correct = 0;
    let total = 0;
    for (const state of Object.values(questionStates)) {
      if (!state.history) continue;
      for (const attempt of state.history) {
        if (attempt.timestamp >= weekStart && attempt.timestamp < weekEnd) {
          total++;
          if (attempt.isCorrect) correct++;
        }
      }
    }
    result.push({
      label: weekLabels[weeks - i] || `${i}w`,
      accuracy: total > 0 ? Math.round(correct / total * 100) : 0,
      questionCount: total
    });
  }
  return result;
}
export interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface StudentDetailPanelProps {
  student: StudentData;
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  onClose: () => void;
  onViewDashboard?: (learnerId: string) => void;
  isOpen: boolean;
  classroomName?: string;
  onRemoveFromClassroom?: (studentId: string) => void;
  onUnlinkStudent?: (studentId: string) => void;
  onNavigateToAssessments?: (studentName: string) => void;
}
export function StudentDetailPanel({
  student,
  selectedGroup,
  getModuleMastery,
  onClose,
  onViewDashboard,
  isOpen,
  classroomName,
  onRemoveFromClassroom,
  onUnlinkStudent,
  onNavigateToAssessments
}: StudentDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overzicht' | 'vakken' | 'activiteit' | 'toets' | 'beheer'>('overzicht');
  const [confirmAction, setConfirmAction] = useState<'remove' | 'unlink' | null>(null);
  const [note, setNote] = useState('');
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | null>(null);
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assessmentReports, setAssessmentReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  /** AS-2026-07-P3-02: distinguish a failed load from "no toetsen yet". */
  const [assessmentLoadError, setAssessmentLoadError] = useState(false);
  const [supportCodeCopied, setSupportCodeCopied] = useState(false);
  const supportCode = pseudonymFor(student.id);
  const handleCopySupportCode = () => {
    navigator.clipboard.writeText(supportCode);
    setSupportCodeCopied(true);
    setTimeout(() => setSupportCodeCopied(false), 2000);
  };
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose);
  const stats = useMemo(() => {
    return getDashboardStats(student.data, MODULES, selectedGroup);
  }, [student.data, selectedGroup]);
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(student.data.questionStates || {});
  }, [student.data.questionStates]);
  const weeklyStats = useMemo(() => {
    return getWeeklyStats(student.data.questionStates || {});
  }, [student.data.questionStates]);
  const moduleMastery = useMemo(() => {
    return MODULES.map(module => {
      const mastery = getModuleMastery(student.id, module.id);
      return {
        module,
        mastery
      };
    }).sort((a, b) => b.mastery - a.mastery);
  }, [student.id, getModuleMastery]);
  const recentSessions = useMemo(() => {
    return getRecentSessionsForLearner(student.data.questionStates || {}, 5);
  }, [student.data.questionStates]);
  const trends = useMemo(() => {
    return getAllModuleTrends(student.data.familyMastery || {}, selectedGroup);
  }, [student.data.familyMastery, selectedGroup]);
  const strengths = moduleMastery.filter(m => m.mastery >= 60).slice(0, 3);
  const weaknesses = [...moduleMastery].reverse().filter(m => m.mastery > 0 && m.mastery < 40).slice(0, 3);
  const differentiationInsights = useMemo(() => {
    return getStudentInsights(student.data, selectedGroup, 3);
  }, [student.data, selectedGroup]);
  const practicedModules = moduleMastery.filter(m => m.mastery > 0);
  const overallMastery = practicedModules.length > 0 ? Math.round(practicedModules.reduce((sum, m) => sum + m.mastery, 0) / practicedModules.length) : 0;
  // Determine Djay's message and emotion
  const getDjayInsight = () => {
    if (differentiationInsights.length > 0) {
      const primaryInsight = differentiationInsights[0];
      return {
        emotion: 'encouraging' as const,
        message: `${student.name} loopt vast bij ${primaryInsight.familyName.toLowerCase()}. ${primaryInsight.recommendation}`
      };
    }
    if (weaknesses.length > 0) {
      return {
        emotion: 'encouraging' as const,
        message: `${student.name} heeft wat extra oefening nodig bij ${weaknesses[0].module.title.toLowerCase()}.`
      };
    }
    if (strengths.length > 0 && overallMastery > 70) {
      return {
        emotion: 'proud' as const,
        message: `Wauw, ${student.name} is ontzettend goed bezig! Vooral ${strengths[0].module.title.toLowerCase()} gaat fantastisch.`
      };
    }
    if (weeklyStats.daysPracticed >= 3) {
      return {
        emotion: 'happy' as const,
        message: `${student.name} heeft een goed oefenritme te pakken deze week!`
      };
    }
    return {
      emotion: 'thinking' as const,
      message: `Laten we samen kijken hoe we ${student.name} verder kunnen helpen groeien.`
    };
  };
  const djayInsight = getDjayInsight();
  // Load notes on mount or student change
  useEffect(() => {
    dataLayer.getTeacherNote(student.id).then(setNote).catch(() => setNote(''));
  }, [student.id]);
  const handleNoteBlur = () => {
    setSaveStatus('saving');
    dataLayer.saveTeacherNote(student.id, note).then(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }).catch(() => {
      setSaveStatus(null);
    });
  };
  // Reset confirmation state when panel closes or student changes
  useEffect(() => {
    setConfirmAction(null);
    if (!isOpen) {
      setActiveTab('overzicht');
    }
  }, [isOpen, student.id]);
  useEffect(() => {
    if (activeTab === 'toets') {
      setIsLoadingAssessments(true);
      setAssessmentLoadError(false);
      // AS-2026-07-P1-01: learner scope enforced by the accessor rather than
      // an ad-hoc filter at this call-site.
      getAssessmentHistoryForLearner(student.id, selectedGroup).then(filteredHistory => {
        setAssessmentHistory(filteredHistory);
        Promise.all(filteredHistory.map(entry => getReport(entry.attemptId).then(report => ({
          id: entry.attemptId,
          report
        })))).then(results => {
          const reportsMap: Record<string, AssessmentReport> = {};
          results.forEach(({
            id,
            report
          }) => {
            if (report) reportsMap[id] = report;
          });
          setAssessmentReports(reportsMap);
          setIsLoadingAssessments(false);
        }).catch(() => {
          // AS-2026-07-P3-02: reports failed — history is still valid, but
          // flag it so the panel does not imply "no results".
          setAssessmentLoadError(true);
          setIsLoadingAssessments(false);
        });
      }).catch(() => {
        setAssessmentLoadError(true);
        setIsLoadingAssessments(false);
      });
    }
  }, [activeTab, selectedGroup, student.id]);
  const TABS: {
    id: 'overzicht' | 'vakken' | 'activiteit' | 'toets' | 'beheer';
    label: string;
  }[] = [{
    id: 'overzicht',
    label: 'Overzicht'
  }, {
    id: 'vakken',
    label: 'Vakken'
  }, {
    id: 'activiteit',
    label: 'Activiteit'
  }, {
    id: 'toets',
    label: 'Toetsen'
  }, {
    id: 'beheer',
    label: 'Beheer'
  }];
  return <AnimatePresence data-id="element-10612" data-mp="5go6u33kjlu0">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40" data-id="element-10613" data-mp="g9ysl1ywbvz" />
        

          {/* Slide-over Panel */}
          <motion.div ref={focusTrapRef as React.RefObject<HTMLDivElement>} initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 right-0 w-full max-w-2xl bg-slate-50 shadow-2xl z-50 flex flex-col border-l border-slate-200 overflow-hidden" data-id="element-10614" data-mp="3nncadoiz00xk">
          
            {/* Header */}
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-10615" data-mp="2zvf1a56wnn05">
              <h2 className="text-lg font-bold text-slate-900" data-id="element-10616" data-mp="1fcusiq4a1rta">
                {student.name}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-10617" data-mp="1jdenpo5ailcs">
              
                <X className="w-5 h-5" data-id="element-10618" data-mp="1tbb4owkwsait" />
              </button>
            </div>

            {/* Tab Bar (Sticky) */}
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 flex items-center gap-6 sticky top-0 z-10" data-id="element-10619" data-mp="10oq6pd9xh06a">
              {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-4 text-sm font-bold transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-sm ${activeTab === tab.id ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-10620" data-mp="15n1fdkohl4mx">
              
                  {tab.label}
                  {activeTab === tab.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" initial={false} data-id="element-10621" data-mp="9x16is03e8t9" />}
                </button>)}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 relative" data-id="element-10622" data-mp="jm15ibelh47g">
              <AnimatePresence mode="wait" data-id="element-10623" data-mp="enr8rwd81vjq">
                {activeTab === 'overzicht' && <motion.div key="overzicht" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-8" data-id="element-10624" data-mp="eqlvjujpjsgb">
                
                    {/* Student Profile Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-10625" data-mp="1cc1asbd0cjau">
                      <div className="flex items-center gap-5" data-id="element-10626" data-mp="17d6v3iac7auy">
                        <div className="relative" data-id="element-10627" data-mp="17cos82t65ye2">
                          <div className="w-20 h-20 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-3xl font-black shadow-md transform -rotate-3" data-id="element-10628" data-mp="2paa8ye2lhojh">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-slate-100" data-id="element-10629" data-mp="2l8yhe305ut8n">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center border-2 border-white" data-id="element-10630" data-mp="38yapezjr4v9d">
                              <span className="text-xs font-black text-emerald-700" data-id="element-10631" data-mp="3czclx8maat7n">
                                {overallMastery}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-10632" data-mp="3rwdh5m3op6av">
                          {/*
                           Dit was een <h1>, terwijl de pagina eronder al een
                           h1 heeft en het paneel zelf met een h2 opent. Een
                           schermlezer kreeg zo twee kopniveaus voor dezelfde
                           leerling en een tweede h1 die niet de titel van de
                           pagina is — dan klopt de inhoudsopgave niet meer
                           waarmee iemand door het scherm navigeert.
                           */}
                          <h3 aria-hidden="true" className="text-3xl font-black text-slate-900 tracking-tight" data-id="element-10633" data-mp="1u4vsw4wxv3p7">
                        
                            {student.name}
                          </h3>
                          <div className="flex items-center gap-3 mt-1.5" data-id="element-10634" data-mp="2i0i0fvt7vzkr">
                            <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-sm font-bold border border-slate-200" data-id="element-10635" data-mp="2hydnkodcbq8c">
                              Groep {selectedGroup}
                            </span>
                            {onViewDashboard && <button onClick={() => onViewDashboard(student.id)} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-10636" data-mp="35pj6jcekuymv">
                          
                                Bekijk als leerling{' '}
                                <ArrowRight className="w-3.5 h-3.5" data-id="element-10637" data-mp="3mzepai47irm6" />
                              </button>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Djay Insight */}
                    <div className="flex items-center gap-4 bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100" data-id="element-10638" data-mp="3vzt9fe5fq0o3">
                      <DjayNarrator size="sm" emotion={djayInsight.emotion} data-id="element-10639" data-mp="4rff8p3z6qnt" />
                      <p className="text-sm font-medium text-indigo-900 leading-relaxed" data-id="element-10640" data-mp="9la7wi1k6fz1">
                        {djayInsight.message}
                      </p>
                    </div>

                    {/* Quick Stats Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-10641" data-mp="nk9kgrbsr4eu">
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10642" data-mp="ni6w0bqaljc8">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2" data-id="element-10643" data-mp="2jl1tjsp8l8ba">
                          <CheckCircle2 className="w-4 h-4" data-id="element-10644" data-mp="2tdr6yt4ckw99" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10645" data-mp="2omr5daka9q0f">
                          {stats.totalQuestions}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10646" data-mp="339kf80b1a3l8">
                          Beantwoord
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10647" data-mp="3cbae1r78w4t1">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2" data-id="element-10648" data-mp="37b5z45lzji4x">
                          <Activity className="w-4 h-4" data-id="element-10649" data-mp="1czhdjkl90hbk" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10650" data-mp="3oml037gtzw8n">
                          {Math.round(stats.accuracy * 100)}%
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10651" data-mp="3tnidz7ut644c">
                          Nauwkeurigheid
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10652" data-mp="3jt89wswi7hds">
                        <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-2" data-id="element-10653" data-mp="3auwor6eepbsq">
                          <Zap className="w-4 h-4" data-id="element-10654" data-mp="2wvx03yqw8wwk" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10655" data-mp="30wgyf5n7e6ka">
                          {stats.currentStreak}
                        </div>
                        {/* Was "Dagen Streak", maar `stats.currentStreak` telt
                            opeenvolgende GOEDE ANTWOORDEN (computeCurrentStreak),
                            geen dagen. Een leerkracht las hier dus 12 "dagen" voor
                            een kind dat één sessie had gedaan. Zie utils/streakFacts. */}
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10656" data-mp="39bbk5t5kvm7l">
                          Goed op rij
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10657" data-mp="30an7p2vu1twt">
                        <div className="w-8 h-8 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center mb-2" data-id="element-10658" data-mp="2vbkau5q0atzx">
                          <Clock className="w-4 h-4" data-id="element-10659" data-mp="2qd907srw0fgn" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10660" data-mp="2lepd3p1k4xmp">
                          {stats.learningTime}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10661" data-mp="jm9kt21h75fh">
                          Minuten
                        </div>
                      </Card>
                    </div>

                    {/* SD-1: Weekly Accuracy Trend */}
                    {(() => {
                const weeklyTrend = getWeeklyAccuracyTrend(student.data.questionStates || {});
                const hasData = weeklyTrend.some(w => w.questionCount > 0);
                if (!hasData) return null;
                return <Card className="p-5 border-slate-200 shadow-sm bg-white" data-id="element-10662" data-mp="957d7ac7npwh">
                          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-10663" data-mp="d5r6u3xksbdv">
                            <BarChart3 className="w-4 h-4 text-indigo-500" data-id="element-10664" data-mp="i42m4u6n3e3h" />
                            Trend afgelopen weken
                          </h3>
                          <div className="flex items-end gap-2 h-28" data-id="element-10665" data-mp="1tk9asxpbrjjl">
                            {weeklyTrend.map((week, i) => {
                      const barHeight = week.questionCount > 0 ? Math.max(week.accuracy / 100 * 100, 8) : 0;
                      const barColor = week.accuracy >= 70 ? 'bg-emerald-500' : week.accuracy >= 40 ? 'bg-amber-400' : week.questionCount > 0 ? 'bg-rose-400' : 'bg-slate-200';
                      return <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-10666" data-mp="3k3gtsh61o0hb" data-mpi={i}>
                              
                                  <span className="text-[10px] font-bold text-slate-600 tabular-nums" data-id="element-10667" data-mp="3p1s40t094d02">
                                    {week.questionCount > 0 ? `${week.accuracy}%` : '—'}
                                  </span>
                                  <div className="w-full flex items-end justify-center h-20" data-id="element-10668" data-mp="2wg8zeua5mbu2">
                                    <motion.div initial={{
                            height: 0
                          }} animate={{
                            height: `${barHeight}%`
                          }} transition={{
                            duration: 0.6,
                            delay: i * 0.08,
                            ease: 'easeOut'
                          }} className={`w-full max-w-[40px] rounded-t-md ${barColor}`} data-id="element-10669" data-mp="31fllvdx45gw4" />
                                
                                  </div>
                                  <span className="text-[9px] font-bold text-slate-400 text-center leading-tight" data-id="element-10670" data-mp="1wec8twprvi97">
                                    {week.label}
                                  </span>
                                </div>;
                    })}
                          </div>
                        </Card>;
              })()}

                    {/* Strengths & Weaknesses */}
                    {(strengths.length > 0 || differentiationInsights.length > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-10671" data-mp="1dfzzoayv28dm">
                        {strengths.length > 0 && <Card className="p-5 border-emerald-100 bg-emerald-50/30 shadow-sm" data-id="element-10672" data-mp="18ihm279wf4za">
                            <h3 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-10673" data-mp="37szj6f6d3mq6">
                              <ArrowUpRight className="w-4 h-4" data-id="element-10674" data-mp="3lzjcu3tzgkif" /> Sterk in
                            </h3>
                            <div className="space-y-2.5" data-id="element-10675" data-mp="3qprfc1l8raw8">
                              {strengths.map(s => {
                      const IconComp = getModuleIcon(s.module.icon);
                      return <div key={s.module.id} className="flex items-center justify-between bg-white px-3 py-2.5 rounded-xl border border-emerald-100 shadow-sm text-sm" data-id="element-10676" data-mp="3cws102x73mq9">
                            
                                    <div className="flex items-center gap-2.5 text-slate-700 font-medium" data-id="element-10677" data-mp="djhl01j6gbsa">
                                      <IconComp className="w-4 h-4 text-emerald-500" data-id="element-10678" data-mp="3vpckwuux2hhx" />
                                      <span className="truncate" data-id="element-10679" data-mp="4it2oumr3p9l">
                                        {s.module.title}
                                      </span>
                                    </div>
                                    <span className="font-black text-emerald-700 ml-2" data-id="element-10680" data-mp="2p8qx57ayouh6">
                                      {s.mastery}%
                                    </span>
                                  </div>;
                    })}
                            </div>
                          </Card>}
                        {differentiationInsights.length > 0 && <Card className="p-5 border-rose-100 bg-rose-50/30 shadow-sm" data-id="element-10681" data-mp="2qpnvejbh31jd">
                            <h3 className="text-xs font-bold text-rose-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-10682" data-mp="2lse9hdnal80a">
                              <AlertTriangle className="w-4 h-4" data-id="element-10683" data-mp="1xzfnrlekbzj1" />{' '}
                              Differentiatie Inzicht
                            </h3>
                            <div className="space-y-3" data-id="element-10684" data-mp="27sd71trdpsip">
                              {differentiationInsights.map(insight => {
                      const moduleDef = MODULES.find(m => m.id === insight.moduleId);
                      const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
                      return <div key={insight.familyId} className="bg-white p-3 rounded-xl border border-rose-100 shadow-sm text-sm flex flex-col gap-2" data-id="element-10685" data-mp="1aawfh5vijmqy">
                            
                                    <div className="flex items-center justify-between" data-id="element-10686" data-mp="1swyblaxg8xj1">
                                      <div className="flex items-center gap-2.5 text-slate-700 font-medium" data-id="element-10687" data-mp="1xwau5jhafift">
                                        <IconComp className="w-4 h-4 text-rose-500" data-id="element-10688" data-mp="22nsz0imfm362" />
                                        <span className="truncate font-bold" data-id="element-10689" data-mp="26pnxlfcza2g5">
                                          {insight.familyName}
                                        </span>
                                      </div>
                                      <span className="font-black text-rose-600 ml-2" data-id="element-10690" data-mp="2gon5uwttzw2m">
                                        {insight.mastery}%
                                      </span>
                                    </div>
                                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-10691" data-mp="mbnl6palgboh">
                                      {insight.recommendation}
                                    </p>
                                  </div>;
                    })}
                            </div>
                          </Card>}
                      </div>}
                  </motion.div>}

                {activeTab === 'vakken' && <motion.div key="vakken" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-10692" data-mp="2vnzdcp8mxyj0">
                
                    {/* Module Mastery List */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden" data-id="element-10693" data-mp="3ef0bxc5ufs21">
                      <div className="p-5 border-b border-slate-100 bg-white" data-id="element-10694" data-mp="39gysur8f3sqk">
                        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-10695" data-mp="231y3j4dwe8sy">
                          <Target className="w-5 h-5 text-indigo-500" data-id="element-10696" data-mp="1y1jwnpo194wi" />
                          Vakbeheersing
                        </h3>
                      </div>

                      <div className="divide-y divide-slate-100 bg-white" data-id="element-10697" data-mp="24iv971gqni56">
                        {practicedModules.length > 0 ? practicedModules.map(({
                    module,
                    mastery
                  }) => {
                    const trend = trends[module.id];
                    const IconComp = getModuleIcon(module.icon);
                    const barColor = mastery >= 60 ? 'bg-emerald-500' : mastery >= 40 ? 'bg-amber-400' : 'bg-rose-500';
                    const textColor = mastery >= 60 ? 'text-emerald-700' : mastery >= 40 ? 'text-amber-600' : 'text-rose-600';
                    return <div key={module.id} className="p-4 hover:bg-slate-50 transition-colors flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-10698" data-mp="1btwllotujnsh">
                          
                                <div className="flex items-center gap-3 sm:w-1/3 flex-shrink-0" data-id="element-10699" data-mp="xwrahaf9zyqd">
                                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-10700" data-mp="12vc8d37226bn">
                                    <IconComp className="w-4 h-4 text-slate-500" data-id="element-10701" data-mp="e94x3871rdoa" />
                                  </div>
                                  <div className="font-bold text-slate-800 text-sm truncate" data-id="element-10702" data-mp="t6ngrw4vb0ov">
                                    {module.title}
                                  </div>
                                </div>

                                <div className="flex-1 flex items-center gap-4" data-id="element-10703" data-mp="124r48xzey7jb">
                                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-10704" data-mp="x6pgw0uyfm21">
                                    <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${mastery}%`
                          }} transition={{
                            duration: 0.8,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${barColor}`} data-id="element-10705" data-mp="1bvu4xyofkki2" />
                              
                                  </div>
                                  <div className={`w-10 text-right font-black text-sm ${textColor}`} data-id="element-10706" data-mp="2s8prt7kgdh3o">
                              
                                    {mastery}%
                                  </div>
                                  <div className="w-20 flex justify-end" data-id="element-10707" data-mp="318w1sjgxzyy3">
                                    {trend === 'up' && <span className="text-[10px] font-bold text-emerald-700 flex items-center bg-emerald-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-10708" data-mp="24kzgx2eh2hh2">
                                        <ArrowUpRight className="w-3 h-3 mr-1" data-id="element-10709" data-mp="2dk32cd1438yq" />{' '}
                                        Stijgt
                                      </span>}
                                    {trend === 'down' && <span className="text-[10px] font-bold text-rose-600 flex items-center bg-rose-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-10710" data-mp="1ltyl2n8nf21q">
                                        <ArrowDownRight className="w-3 h-3 mr-1" data-id="element-10711" data-mp="1psnuy5uha1gm" />{' '}
                                        Daalt
                                      </span>}
                                    {trend === 'stable' && <span className="text-[10px] font-bold text-slate-400 flex items-center bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-10712" data-mp="1dali7d0efs54">
                                        <ArrowRight className="w-3 h-3 mr-1" data-id="element-10713" data-mp="14b85mbgzang6" />{' '}
                                        Stabiel
                                      </span>}
                                  </div>
                                </div>
                              </div>;
                  }) : <div className="p-8 text-center text-slate-500" data-id="element-10714" data-mp="pnvgydvvk1lk">
                            Nog geen vakken geoefend.
                          </div>}
                      </div>
                    </Card>
                  </motion.div>}

                {activeTab === 'activiteit' && <motion.div key="activiteit" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10715" data-mp="bmj2k6wd481s">
                
                    {/* Recent Activity */}
                    <Card className="p-6 border-slate-200 shadow-sm" data-id="element-10716" data-mp="3lr1ryx7l5tlg">
                      <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-10717" data-mp="2xzmkkq0ycm3c">
                        <Activity className="w-5 h-5 text-indigo-500" data-id="element-10718" data-mp="2t1l0c1rsigqa" />
                        Activiteit deze week
                      </h3>
                      {weeklyStats.daysPracticed > 0 ? <div className="flex justify-center" data-id="element-10719" data-mp="32z90x8c2uigd">
                          <WeeklyActivityDots weeklyActivity={weeklyActivity} color="indigo" data-id="element-10720" data-mp="32xo1b4awo9bn" />
                    
                        </div> : <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-10721" data-mp="2f7bmral5nec3">
                          <DjayNarrator size="sm" emotion="sleeping" data-id="element-10722" data-mp="2a8qvw1rsm70b" />
                          <p className="text-sm font-medium text-slate-500 mt-3" data-id="element-10723" data-mp="pqzjctqhzixc">
                            Nog geen activiteit voor deze leerling
                          </p>
                        </div>}
                    </Card>

                    <Card className="p-6 border-slate-200 shadow-sm" data-id="element-10724" data-mp="ktfxb7nk61uu">
                      <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-10725" data-mp="ktpnav8ls90j">
                        <Clock className="w-5 h-5 text-indigo-500" data-id="element-10726" data-mp="18gxtdfduvm3m" />
                        Laatste sessies
                      </h3>
                      {recentSessions.length > 0 ? <ActivityTimeline sessions={recentSessions} data-id="element-10727" data-mp="zhascup1gib3" /> : <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-10728" data-mp="zkosdwa8cffd">
                          <DjayNarrator size="sm" emotion="sleeping" data-id="element-10729" data-mp="14j9wi6slrj93" />
                          <p className="text-sm font-medium text-slate-500 mt-3" data-id="element-10730" data-mp="3k707l8tnm64b">
                            Nog geen sessies gevonden
                          </p>
                        </div>}
                    </Card>
                  </motion.div>}

                {activeTab === 'toets' && <motion.div key="toets" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-10731" data-mp="1pte66nxjkqq4">
                
                    {/* Insight Bar */}
                    <InsightBar message={assessmentHistory.length === 0 ? `${student.name} heeft nog geen toetsen gemaakt.` : `${student.name} heeft ${assessmentHistory.length} toets(en) gemaakt. Resultaten geven een indicatie van het niveau op basis van de Meester djay taxonomie.`} data-id="element-10732" data-mp="170trqbilao5u" />
                

                    <Card className="border-slate-200 shadow-sm overflow-hidden" data-id="element-10733" data-mp="2wfrwg6zp6pcq">
                      <div className="p-5 border-b border-slate-100 bg-white flex items-center justify-between" data-id="element-10734" data-mp="2seod3aifyb6r">
                        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-10735" data-mp="e6dttf7oss84">
                          <ClipboardCheck className="w-5 h-5 text-indigo-500" data-id="element-10736" data-mp="j5i2m4qp8nxx" />
                          Toetsresultaten
                        </h3>
                        {onNavigateToAssessments && assessmentHistory.length > 0 && <Button variant="outline" size="sm" onClick={() => onNavigateToAssessments(student.name)} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-id="element-10737" data-mp="rxk0pwdaps51">
                      
                              <ArrowRight className="w-4 h-4 mr-2" data-id="element-10738" data-mp="87bx8j4v2u3" />
                              Bekijk alle toetsen
                            </Button>}
                      </div>

                      <div className="divide-y divide-slate-100 bg-white" data-id="element-10739" data-mp="99ewyown2768">
                        {isLoadingAssessments ? <div className="p-8 text-center text-slate-500" data-id="element-10740" data-mp="1fnecsbpsjco8">
                            Laden...
                          </div> : assessmentLoadError && assessmentHistory.length === 0 ? (/* AS-2026-07-P3-02: failed read ≠ "geen toetsen". */
                  <div className="p-8 text-center" role="alert" data-id="element-10741" data-mp="1j86k3ztqgr2q">
                            <p className="font-bold text-slate-900 mb-1" data-id="element-10742" data-mp="1e93h0kxbcuxe">
                              Toetsgegevens konden niet geladen worden
                            </p>
                            <p className="text-sm text-slate-500" data-id="element-10743" data-mp="1o4na6zkhtfig">
                              Er is niets verwijderd. Open het tabblad opnieuw
                              om het nog eens te proberen.
                            </p>
                          </div>) : assessmentHistory.length > 0 ? assessmentHistory.map(entry => {
                    const report = assessmentReports[entry.attemptId];
                    return <div key={entry.attemptId} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-10744" data-mp="26x94vgjnz01g">
                          
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-10745" data-mp="2ktcvkzvbwq96">
                                  <div data-id="element-10746" data-mp="2zinzish6d2tk">
                                    <div className="font-bold text-slate-900" data-id="element-10747" data-mp="2zixoqdueb5y7">
                                      {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                                    </div>
                                    <div className="text-sm text-slate-500 mt-1" data-id="element-10748" data-mp="2gqvd5gwm2fut">
                                      {entry.totalQuestions} vragen •{' '}
                                      {Math.round(entry.totalTimeSeconds / 60)}{' '}
                                      minuten
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4" data-id="element-10749" data-mp="1xtamc4aad77k">
                                    <div className="text-right" data-id="element-10750" data-mp="1xvetcv3hemc2">
                                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10751" data-mp="22rm18tffu7wn">
                                        Score
                                      </div>
                                      <div className={`text-xl font-black ${entry.score >= 70 ? 'text-emerald-600' : entry.score >= 50 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-10752" data-mp="3jnchneqax1f">
                                  
                                        {entry.score}%
                                      </div>
                                    </div>
                                    {report && <div className="text-right" data-id="element-10753" data-mp="mbg7toh9sucb">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10754" data-mp="ia317c6csn4c">
                                          Niveau
                                        </div>
                                        <div className="flex items-center justify-end gap-2" data-id="element-10755" data-mp="33027zibnwq5d">
                                          <div className="text-xl font-black text-indigo-600" data-id="element-10756" data-mp="2y18v9s479369">
                                            {report.overallEstimatedLevelCode}
                                          </div>
                                          {(() => {
                                const cito = levelCodeToCitoLevel(report.overallEstimatedLevelCode, report.selectedGroup);
                                if (!cito) return null;
                                return <div className={`text-xs font-bold px-2 py-0.5 rounded border ${cito.color} ${cito.bgColor} ${cito.borderColor}`} data-id="element-10757" data-mp="1rc61dkjbbwj3">
                                        
                                                {cito.label}
                                              </div>;
                              })()}
                                        </div>
                                      </div>}
                                  </div>
                                </div>

                                {report && report.byModule && report.byModule.length > 0 && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-10758" data-mp="fuo6lajpm78p">
                                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-10759" data-mp="3t41xb9erdj86">
                                        Per Vak
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-10760" data-mp="5rr9qfj9tfad">
                                        {report.byModule.map(m => {
                            const moduleInfo = MODULES.find(mod => mod.id === m.moduleId);
                            if (!moduleInfo) return null;
                            const IconComp = getModuleIcon(moduleInfo.icon);
                            const cito = levelCodeToCitoLevel(m.estimatedLevelCode ?? '', report.selectedGroup);
                            return <div key={m.moduleId} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3" data-id="element-10761" data-mp="3e71gw220pap0">
                                    
                                              <div className="flex items-center gap-2" data-id="element-10762" data-mp="thvbehcwf4yq">
                                                <IconComp className="w-4 h-4 text-slate-400" data-id="element-10763" data-mp="11xkw1ol5levx" />
                                                <span className="text-sm font-bold text-slate-700" data-id="element-10764" data-mp="15zfrwdkbf02b">
                                                  {moduleInfo.title}
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-3" data-id="element-10765" data-mp="x0uesxxm4d8x">
                                                <span className="text-sm font-black text-slate-900" data-id="element-10766" data-mp="1kqoqhc673vrl">
                                                  {Math.round(m.scorePercentage ?? m.accuracy)}
                                                  %
                                                </span>
                                                <div className="flex items-center gap-1.5" data-id="element-10767" data-mp="1fviv7mqwl4qu">
                                                  <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100" data-id="element-10768" data-mp="23ld8g5f4wvbm">
                                                    {m.estimatedLevelCode}
                                                  </span>
                                                  {cito && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${cito.color} ${cito.bgColor} ${cito.borderColor}`} data-id="element-10769" data-mp="3c25e78xgzxks">
                                          
                                                      {cito.label}
                                                    </span>}
                                                </div>
                                              </div>
                                            </div>;
                          })}
                                      </div>
                                    </div>}
                              </div>;
                  }) : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-10770" data-mp="dm2skwvm334c">
                            <div className="mb-3" data-id="element-10771" data-mp="idun1ihw1szr">
                              <DjayNarrator size="md" emotion="encouraging" data-id="element-10772" data-mp="ienkttyvd85r" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-10773" data-mp="rq1uipcnf1ck">
                              Nog geen toetsen
                            </h4>
                            <p className="text-xs text-slate-500 max-w-[220px]" data-id="element-10774" data-mp="11gmofzhdnhp5">
                              {student.name} heeft nog geen toetsen gemaakt. Zet
                              een toets klaar of laat een niveaubepaling doen!
                            </p>
                          </div>}
                      </div>
                    </Card>
                  </motion.div>}

                {activeTab === 'beheer' && <motion.div key="beheer" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-10775" data-mp="it16wkxel7a6">
                
                    <div className="mb-8" data-id="element-10776" data-mp="1x885vlox7wzo">
                      <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-10777" data-mp="1x76jil2gfo2n">
                        <StickyNote className="w-5 h-5 text-indigo-500" data-id="element-10778" data-mp="218iaov0z8hcy" />
                        Persoonlijke Notities
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed mb-3" data-id="element-10779" data-mp="2673e13bizh5m">
                        Deze notities zijn alleen zichtbaar voor jou. Gebruik
                        dit voor observaties of aandachtspunten.
                      </p>
                      <div className="relative" data-id="element-10780" data-mp="1ikd8vt3vr6gs">
                        <textarea value={note} onChange={e => setNote(e.target.value)} onBlur={handleNoteBlur} placeholder="Typ hier je notities over deze leerling..." className="w-full h-32 p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none" data-id="element-10781" data-mp="1nieuolsoxjvy" />
                    
                        <AnimatePresence data-id="element-10782" data-mp="s9b7a7o4pql2">
                          {saveStatus === 'saved' && <motion.div initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="absolute bottom-3 right-3 flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md" data-id="element-10783" data-mp="s8jmlrw2ld9k">
                        
                              <CheckCircle2 className="w-3 h-3" data-id="element-10784" data-mp="4jgxjyz3wi51" />
                              Opgeslagen
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/*
                      ─── SUPPORTNUMMER ───────────────────────────────────────
                      Staat hier en niet in "Overzicht": Overzicht gaat over hoe
                      het met dit kind gaat, dit is een administratief gegeven.
                       Het bestaat omdat support de naam van een leerling niet te
                      zien krijgt — daar staan pseudoniemen. Zonder dit nummer
                      moet een leerkracht een naam doorgeven of moet support
                      gokken welk pseudoniem bedoeld is, en dan wordt de
                      afscherming in de praktijk omzeild in plaats van
                      gerespecteerd.
                       Afgeleid uit het account-id (`pseudonymFor`), nergens
                      opgeslagen. Wat de leerkracht hier leest is daarom per
                      definitie hetzelfde als wat de beheerder in het
                      adminportaal ziet — er is geen tweede waarde die kan gaan
                      afwijken.
                     */}
                    <div className="mb-8" data-id="element-10785" data-mp="1cync1kid7u2d">
                      <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-10786" data-mp="1cyvnr92r4tbz">
                        <LifeBuoy className="w-5 h-5 text-slate-400" data-id="element-10787" data-mp="1gzfm2fz2a2zp" />
                        Supportnummer
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed mb-3" data-id="element-10788" data-mp="2xgem8lw6u626">
                        Geef dit nummer door als je hulp vraagt over deze
                        leerling. Zo weet support om wie het gaat zonder dat je
                        de naam hoeft te delen.
                      </p>
                      <div className="flex items-center gap-2" data-id="element-10789" data-mp="3l5qrjaoe9tz7">
                        <span className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 font-mono text-sm font-bold tracking-wider text-slate-700" data-id="element-10790" data-mp="3l460hv1agt63">
                          {supportCode}
                        </span>
                        <button onClick={handleCopySupportCode} aria-label="Kopieer supportnummer" className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" data-id="element-10791" data-mp="3p7kn54jqg7si">
                          {supportCodeCopied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-10792" data-mp="3uw2h9axqwu7k" /> : <Copy className="w-4 h-4" data-id="element-10793" data-mp="3uxdpbmy84o4h" />}
                        </button>
                      </div>
                    </div>

                    <div className="mb-6" data-id="element-10794" data-mp="8oui4qdf5jjz">
                      <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-10795" data-mp="8nt7gn0bez2s">
                        Leerlingbeheer
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed" data-id="element-10796" data-mp="hpajnualqgm7">
                        Beheer de status van deze leerling. Voor het verwijderen
                        of ontkoppelen van leerlingen, ga naar de Klas Beheer
                        pagina.
                      </p>
                    </div>

                    {/* Danger Zone / Beheer */}
                    <div className="space-y-3" data-id="element-10797" data-mp="wddqt70drycl">
                      {classroomName && onRemoveFromClassroom && <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10798" data-mp="10hd5rl60ip3u">
                          <div data-id="element-10799" data-mp="2pxfdbxsy1j4z">
                            <div className="font-bold text-slate-900 text-sm" data-id="element-10800" data-mp="2pxp3bldznqao">
                              Verwijder uit klas
                            </div>
                            <div className="text-xs text-slate-500 mt-1" data-id="element-10801" data-mp="2gvxyeir9vnhv">
                              Haal {student.name} uit de klas "{classroomName}".
                              De leerling blijft wel gekoppeld aan jouw account.
                            </div>
                          </div>

                          {confirmAction === 'remove' ? <div className="flex items-center gap-2 shrink-0" data-id="element-10802" data-mp="34n3ff18y8ltr">
                              <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)} data-id="element-10803" data-mp="2zuab2ehe7fi7">
                        
                                Annuleer
                              </Button>
                              <Button className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" onClick={() => {
                      onRemoveFromClassroom(student.id);
                      setConfirmAction(null);
                    }} data-id="element-10804" data-mp="3scfwenaipy6m">
                        
                                Bevestig
                              </Button>
                            </div> : <Button variant="secondary" size="sm" className="shrink-0 text-slate-600 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200" onClick={() => setConfirmAction('remove')} data-id="element-10805" data-mp="2lyx9mpppy1n5">
                      
                              <UserMinus className="w-4 h-4 mr-2" data-id="element-10806" data-mp="3enb6h252mrx4" />
                              Verwijder uit klas
                            </Button>}
                        </div>}

                      {onUnlinkStudent && <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10807" data-mp="35no2q9ovda0w">
                          <div data-id="element-10808" data-mp="39qcfgsgpf135">
                            <div className="font-bold text-rose-700 text-sm" data-id="element-10809" data-mp="1u77vv4rds1pn">
                              Ontkoppel leerling
                            </div>
                            <div className="text-xs text-slate-500 mt-1" data-id="element-10810" data-mp="1y78aoqie6ex8">
                              Verbreek de koppeling met {student.name} volledig.
                              Je hebt hierna geen toegang meer tot de voortgang.
                            </div>
                          </div>

                          {confirmAction === 'unlink' ? <div className="flex items-center gap-2 shrink-0" data-id="element-10811" data-mp="1vodaiin83w7s">
                              <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)} data-id="element-10812" data-mp="17zbwmfvmfsv4">
                        
                                Annuleer
                              </Button>
                              <Button className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" onClick={() => {
                      onUnlinkStudent(student.id);
                      setConfirmAction(null);
                    }} data-id="element-10813" data-mp="1hoviluems2u7">
                        
                                Bevestig ontkoppeling
                              </Button>
                            </div> : <Button variant="secondary" size="sm" className="shrink-0 text-rose-600 hover:bg-rose-50 border-rose-200" onClick={() => setConfirmAction('unlink')} data-id="element-10814" data-mp="k7nbbjh7w4no">
                      
                              <Unlink className="w-4 h-4 mr-2" data-id="element-10815" data-mp="3b2tpac8xm803" />
                              Ontkoppel
                            </Button>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}